import { useState } from 'react';
import { useForm } from '@mantine/form';

import { useAppSelector, selectCurrencies, useShowResponseStatus } from '@/shared';
import {
  useCreateConversionPairMutation,
  useGetConversionPairRateQuery,
} from '../store/conversion-pairs-api';
import { handleConversionPairError } from '../utils/helpers/conversion-pairs-errors';

import type { TConversionPairCreateParams } from '../store/types';

type FormData = TConversionPairCreateParams;

const initialValues: FormData = {
  baseCurrency: '',
  quoteCurrency: '',
  markupPercent: 0,
  autoRefresh: true,
};

export const useCreateConversionPair = (onClose: () => void) => {
  const avalableCurrencies = useAppSelector(selectCurrencies);
  const [baseCurrency, setBaseCurrency] = useState(initialValues.baseCurrency);
  const [quoteCurrency, setQuoteCurrency] = useState(initialValues.quoteCurrency);
  const [autoRefresh, setAutoRefresh] = useState(initialValues.autoRefresh);

  const [createConversionPair, { isLoading, isSuccess, error }] = useCreateConversionPairMutation();
  const createError = handleConversionPairError(error);

  const isRateSkipped = !autoRefresh || !baseCurrency || !quoteCurrency || baseCurrency === quoteCurrency;
  const {
    data: marketRate,
    isFetching: isRateLoading,
    error: rateError,
  } = useGetConversionPairRateQuery(
    { baseCurrency, quoteCurrency },
    {
      skip: isRateSkipped,
      pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
    },
  );
  const getRateError = handleConversionPairError(rateError);

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues,
    validate: {
      baseCurrency: (value) => !value ? 'Выберите валюту счёта' : null,
      quoteCurrency: (value, values) => {
        if (!value) {
          return 'Выберите валюту оплаты';
        }

        return value === values.baseCurrency ? 'Валюты пары должны отличаться' : null;
      },
      rate: (value, values) => !values.autoRefresh && (!value || value <= 0) ? 'Введите курс' : null,
      markupPercent: (value) => value <= -100 ? 'Наценка должна быть больше -100%' : null,
    },
  });

  form.watch('baseCurrency', ({ value }) => {
    setBaseCurrency(value);
  });

  form.watch('quoteCurrency', ({ value }) => {
    setQuoteCurrency(value);
  });

  form.watch('autoRefresh', ({ value }) => {
    setAutoRefresh(value);
  });

  useShowResponseStatus({
    isSuccess,
    successMessage: 'Торговая пара успешно добавлена',
    error: createError,
  });

  useShowResponseStatus({
    error: getRateError,
  });

  const handleSubmit = async (values: FormData) => {
    if (values.autoRefresh && !marketRate) {
      form.setFieldError('quoteCurrency', 'Не удалось получить курс для выбранной пары');

      return;
    }

    const params: TConversionPairCreateParams = {
      ...values,
      rate: values.autoRefresh ? undefined : values.rate,
    };

    const result = await createConversionPair(params);
    if ('error' in result) {
      return;
    }

    onClose();
  };

  return {
    avalableCurrencies,
    form,
    marketRate,
    isRateLoading,
    isLoading,
    autoRefresh,
    handleSubmit,
  };
};
